// ajay:code all 27-08-26
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Eye, Pencil } from "lucide-react";
import { productAPI, controlPlanAPI } from "../../services/api";

export default function ControlPlanTemplate() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    productAPI.list().then((r) => setProducts(r.data || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: pageSize, offset: (page - 1) * pageSize };
    if (status) params.status = status;
    if (search.trim()) params.search = search.trim();
    controlPlanAPI
      .listDocuments(params)
      .then((r) => {
        setDocuments(r.data.documents || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [status, search]);

  const finalize = async (id) => {
    if (
      !window.confirm(
        "Finalize this Control Plan? Once finalized it can NEVER be edited again, by anyone.",
      )
    )
      return;
    await controlPlanAPI.finalizeDocument(id);
    load();
  };

  return (
    <div style={{ padding: 28 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Control Plan</h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Process control documentation
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate("/company/control-plan/new")}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={14} /> Add Control Plan
        </button>
      </div>

      <div
        className="card"
        style={{
          marginBottom: 20,
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          alignItems: "flex-end",
        }}
      >
        <div
          className="form-group"
          style={{ minWidth: 240, flex: "1 1 240px" }}
        >
          <label>Search (Product ID / Name)</label>
          <input
            type="text"
            placeholder="e.g. P001 or Part1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ minWidth: 160 }}>
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Final">Final</option>
          </select>
        </div>
        {(search || status) && (
          <button
            className="btn-secondary"
            onClick={() => {
              setSearch("");
              setStatus("");
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Control Plan No.</th>
                <th>Product</th>
                <th>Stage</th>
                <th>Date</th>
                <th>Rev No.</th>
                <th>Status</th>
                <th style={{ width: 90 }}>View</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    No Control Plan documents found
                  </td>
                </tr>
              )}
              {documents.map((d) => (
                <tr key={d.id}>
                  <td className="mono">{d.control_plan_no || "—"}</td>
                  <td style={{ fontWeight: 500 }}>
                    {d.product_name}{" "}
                    <span className="text3 mono" style={{ fontSize: 11 }}>
                      ({d.product_code})
                    </span>
                  </td>
                  <td className="text2">{d.stage || "—"}</td>
                  <td>
                    {d.date ? new Date(d.date).toLocaleDateString() : "—"}
                  </td>
                  <td className="text2">{d.rev_no || "—"}</td>
                  <td>
                    <select
                      value={d.status}
                      disabled={d.status === "Final"}
                      onChange={(e) => {
                        if (e.target.value === "Final") finalize(d.id);
                      }}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 6,
                        background:
                          d.status === "Final"
                            ? "rgba(0,224,150,0.12)"
                            : "rgba(255,77,106,0.12)",
                        color:
                          d.status === "Final"
                            ? "var(--success)"
                            : "var(--danger)",
                        border: `1px solid ${d.status === "Final" ? "var(--success)" : "var(--danger)"}`,
                        cursor:
                          d.status === "Final" ? "not-allowed" : "pointer",
                      }}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Final">Final</option>
                    </select>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        title="View"
                        onClick={() =>
                          navigate(`/company/control-plan/${d.id}`)
                        }
                        style={iconBtn}
                      >
                        <Eye size={13} />
                      </button>
                      {d.status === "Draft" && (
                        <button
                          title="Edit"
                          onClick={() =>
                            navigate(`/company/control-plan/${d.id}/edit`)
                          }
                          style={iconBtn}
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > pageSize && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 14,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{" "}
            {total}
          </span>
          <button
            className="btn-secondary"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <button
            className="btn-secondary"
            disabled={page * pageSize >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: 6,
  padding: 5,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
